import { Link } from "react-router-dom";
import { ArrowRight, Calculator } from "lucide-react";
import Seo from "@/components/Seo";
import { CALCULATORS, CHECKED_ON } from "@/data/calculators";

export default function CalculatorsHub() {
  const origin = window.location.origin;
  const pageUrl = `${origin}/calculators`;
  return (
    <main data-testid="calculators-hub">
      <Seo
        title="Free UK Employment Calculators for Employers, 2026 | Electra HR"
        siteName="Electra HR"
        description="Free UK employment calculators for small employers: holiday entitlement and the 12.07% rule, redundancy pay, notice periods and pro-rata salary. Updated for 2026 rates, with the law explained."
        image="/og/default.jpg"
        jsonLd={{
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": "CollectionPage",
              "@id": pageUrl,
              url: pageUrl,
              name: "Free UK employment calculators",
              isPartOf: { "@id": `${origin}/#website` },
              publisher: { "@id": `${origin}/#organization` },
              dateModified: CHECKED_ON.iso,
            },
            {
              "@type": "ItemList",
              itemListElement: CALCULATORS.map((c, i) => ({
                "@type": "ListItem",
                position: i + 1,
                name: c.name,
                url: `${origin}/calculators/${c.slug}`,
              })),
            },
            {
              "@type": "BreadcrumbList",
              itemListElement: [
                { "@type": "ListItem", position: 1, name: "Home", item: origin },
                { "@type": "ListItem", position: 2, name: "Calculators", item: pageUrl },
              ],
            },
          ],
        }}
      />

      <section className="grain relative overflow-hidden bg-ink pb-16 pt-32 sm:pt-40 text-paper">
        <div className="container-x">
          <p className="eyebrow text-pink">Free tools for employers</p>
          <h1 className="mt-5 max-w-4xl font-display text-4xl sm:text-5xl lg:text-6xl font-semibold leading-[1.04] tracking-tight">
            UK employment calculators, <span className="text-pink">with the working shown.</span>
          </h1>
          <p className="mt-6 max-w-2xl text-base sm:text-lg leading-relaxed text-paper/70">
            The sums small employers get asked about most, worked out to the statutory rules and 2026 rates, with the
            legislation behind every step. No sign-up, and nothing you enter leaves your browser.
          </p>
          <p className="mt-5 font-mono text-xs uppercase tracking-[0.18em] text-paper/45">Figures checked {CHECKED_ON.label}</p>
        </div>
      </section>

      <section className="bg-paper py-16 sm:py-24">
        <div className="container-x grid gap-6 md:grid-cols-2">
          {CALCULATORS.map((c) => (
            <Link
              key={c.slug}
              to={`/calculators/${c.slug}`}
              data-testid={`hub-${c.slug}`}
              className="group border-2 border-ink bg-white p-7 transition-all duration-300 hover:-translate-y-1 hover:shadow-[6px_6px_0_0_#F472B6]"
            >
              <Calculator size={24} className="text-pink" />
              <h2 className="mt-5 font-display text-2xl font-semibold tracking-tight">{c.name}</h2>
              <p className="mt-3 text-sm leading-relaxed text-ink/70">{c.blurb}</p>
              <span className="mt-6 inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.18em] text-ink/60 group-hover:text-ink">
                Open calculator <ArrowRight size={14} className="transition-transform duration-300 group-hover:translate-x-1" />
              </span>
            </Link>
          ))}
        </div>
        <div className="container-x mt-12">
          <div className="border-l-4 border-pink bg-pink/10 p-6 text-base leading-relaxed text-ink/80">
            Calculators give the statutory minimum. Real cases have contracts, history and people in them.{" "}
            <Link to="/contact" className="font-semibold underline decoration-pink decoration-2 underline-offset-4">Talk to Electra HR</Link>{" "}
            for a same-day answer at a fixed fee.
          </div>
        </div>
      </section>
    </main>
  );
}
